/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { IonContent, IonPage, IonIcon } from '@ionic/react';
import { sparkles } from 'ionicons/icons';
import {
  Chip, Divider, Typography, Stack,
} from '@mui/material';
import { motion } from 'framer-motion';
import useFetch, { CachePolicies } from 'use-http';
import PostIndicator from 'components/PostIndicator';
import Header from 'components/Header';
import Footer from 'components/Footer';
import WholeMeLoadingCircle from 'pages/wholeme/molecules/wholeMeLoadingCircle/WholeMeLoadingCircle';
import './Goals.css';
import appStrings from 'common/app_strings';
import { displayName } from 'helpers/textManipulation';
import { formatDistanceToNowStrict, parseISO } from 'date-fns';
import appColors from 'common/app_colors';
import AvatarRole from 'components/AvatarRole';

const { goals: strings } = appStrings;
const { text, buttons, container } = appColors;

const updatesChipStyles = {
  fontWeight: 700,
  fontSize: '14px',
  backgroundColor: container.beige,
  color: text.blackText,
  border: `2px solid ${text.blackText}`,
};

const noUpdatesChipStyles = {
  backgroundColor: buttons.lightGrey,
  border: 'none',
  color: text.lightBlack,
};

/**
 * This component has been refactored to act as the generic User Goals list.
 * This will show a list of goals for a user, with no team filter.
 *
 * TODO: rename and move this file away from the 'backr' directory/namespace.  It should live with other goal related screens.
 */
const UserGoalsList = ({ showHeader = true, userId = null }) => {
  const [goals, setGoals] = useState([]);
  const [isInitiallyLoaded, setIsInitiallyLoaded] = useState(false);
  const history = useHistory();
  const params = useParams();
  const subjectUserId = userId ?? params?.id;

  const { get: fetchGoals, loading: isGoalsLoading } = useFetch(`/goals/user/${subjectUserId}`, {
    cachePolicy: CachePolicies.NO_CACHE,
  });

  const goToRelatedPost = (id) => {
    history.push(`/posts/${id}`);
  };

  const fetchInitialData = async () => {
    const allGoals = await fetchGoals();
    return { goals: allGoals };
  };

  const RenderContent = (
    <div>
      {(isGoalsLoading) && !isInitiallyLoaded ? (
        <WholeMeLoadingCircle />
      ) : (
        <motion.div
          className="goals-backr-list"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {
            goals?.map((goal) => (
              <React.Fragment key={goal.id}>
                <div className="goals-item">
                  <div className="goals-item-description goals-item-description-list">
                    <AvatarRole user={goal?.user} medium unsetTopMargin />
                    <div style={{ width: '100%' }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            gap: '0.5rem',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: text.darkNavy,
                            }}
                          >
                            {displayName(goal?.user?.first_name, goal?.user?.last_name)}
                          </Typography>
                          <span className="goals-post-time">
                            {formatDistanceToNowStrict(parseISO(goal?.created_at), {
                              addSuffix: true,
                            })}
                          </span>
                        </div>
                        <div>
                          <PostIndicator text={`${goal?.type} ${strings.labels.goal}`} type={goal?.type} />
                        </div>
                      </div>
                      <Typography className="goals-text goals-text-list" color={text.lightBlack}>
                        {goal?.content}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between' }}>
                        {goal?.post?.id && (
                          <Chip
                            label={strings.seeUpdates(goal?.updatesCount)}
                            sx={{
                              ...updatesChipStyles,
                              ...(goal?.updatesCount === 0 && noUpdatesChipStyles),
                            }}
                            onClick={() => goToRelatedPost(goal?.post?.id)}
                          />
                        )}
                        {goal?.status === 'completed' && (
                          <div>
                            <Typography
                              variant="span"
                              sx={{
                                color: '#2CB669',
                                fontSize: 14,
                                fontWeight: 700,
                                mr: 0.75,
                              }}
                            >
                              {strings.achieved}
                            </Typography>
                            <IonIcon
                              style={{ color: '#2CB669' }}
                              slot="icon-only"
                              icon={sparkles}
                            />
                          </div>
                        )}
                      </Stack>
                    </div>
                  </div>
                </div>
                <Divider sx={{ mt: 2 }} />
              </React.Fragment>
            ))
          }
        </motion.div>
      )}
    </div>
  );

  useEffect(() => {
    fetchInitialData().then((res) => {
      setGoals(res.goals);
      setIsInitiallyLoaded(true);
    });
  }, []);

  return (
    showHeader ? (
      <IonPage>
        <Header />
        <IonContent fullscreen>
          {RenderContent}
        </IonContent>
        <Footer />
      </IonPage>
    ) : RenderContent
  );
};

export default UserGoalsList;
